import type { ThemeConfig } from 'antd'
import { theme as antdTheme } from 'antd'

/**
 * 现代 SaaS Dashboard 主题（Linear/Stripe 风格）
 *
 * 设计原则：
 * - 小圆角（6px 卡片 / 4px 按钮），体现专业感
 * - 清晰的背景层次（4 层：layout < sider < container < elevated）
 * - card 靠边框 + 极弱阴影区分，不依赖强阴影
 * - Indigo 主色（Linear/Stripe 标志色）
 * - Slate 中性色，避免纯灰带来的廉价感
 */

/** 品牌 Emerald（亮色，浅绿色） */
export const BRAND_PRIMARY = '#10B981'
/** 品牌 Emerald（暗色，提亮以保持对比） */
export const BRAND_PRIMARY_DARK = '#34D399'

/**
 * sider/header 专用背景色
 *
 * 设计意图：header 与 sider 使用不同背景色，通过阴影区分层次
 * - 亮色：header #FFFFFF（纯白，浮于 sider 之上），sider #F1F5F9（Slate-100）
 * - 暗色：header #1E293B（Slate-800，略亮），sider #111827（Gray-900）
 *
 * 通过 ThemeProvider 注入为 CSS 变量 --app-bg-sider / --app-bg-header
 */
export const LIGHT_SIDER_BG = '#F1F5F9'
export const DARK_SIDER_BG = '#111827'
export const LIGHT_HEADER_BG = '#FFFFFF'
export const DARK_HEADER_BG = '#1E293B'

/** 亮色主题 Token */
export const lightToken: ThemeConfig['token'] = {
  colorPrimary: BRAND_PRIMARY,
  colorInfo: BRAND_PRIMARY,
  colorSuccess: '#10B981',
  colorWarning: '#F59E0B',
  colorError: '#F43F5E',
  colorLink: BRAND_PRIMARY,

  // 亮色背景层次（从浅到深）：
  //   layout(#F8FAFC) < sider(#FAFBFC) < container/card(#FFFFFF) < elevated(#FFFFFF+阴影)
  colorTextBase: '#0F172A',
  colorBgBase: '#FFFFFF',
  colorBgLayout: '#F8FAFC', // body 背景（最浅灰）
  colorBgContainer: '#FFFFFF', // card 背景（纯白）
  colorBgElevated: '#FFFFFF', // dropdown/modal 背景（纯白 + 阴影）
  colorBorder: '#E2E8F0', // 主边框（Slate-200，清晰可见）
  colorBorderSecondary: '#F1F5F9', // 次级边框（Slate-100，弱分隔）

  // 文本层级
  colorText: '#0F172A',
  colorTextSecondary: '#475569',
  colorTextTertiary: '#64748B',
  colorTextQuaternary: '#94A3B8',

  // 字体
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Source Han Sans CN', sans-serif",
  fontSize: 14,
  fontSizeHeading1: 30,
  fontSizeHeading2: 24,
  fontSizeHeading3: 20,
  fontSizeHeading4: 17,
  fontSizeHeading5: 15,

  // 圆角（Linear 风格的小圆角）
  borderRadius: 6,
  borderRadiusLG: 8,
  borderRadiusSM: 4,
  borderRadiusXS: 3,

  // 阴影（card 用明显阴影，dropdown 用强阴影）
  boxShadow: '0 2px 8px -2px rgba(15, 23, 42, 0.1), 0 1px 3px -1px rgba(15, 23, 42, 0.06)',
  boxShadowSecondary:
    '0 8px 24px -4px rgba(15, 23, 42, 0.14), 0 4px 8px -2px rgba(15, 23, 42, 0.08)',

  // 线宽
  lineWidth: 1,
  lineWidthBold: 1.5,

  // 间距（8px 网格）
  controlHeight: 36,
  controlHeightLG: 44,
  controlHeightSM: 28,

  // 动画
  motionDurationFast: '0.15s',
  motionDurationMid: '0.2s',
  motionDurationSlow: '0.3s',
  motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  motionEaseOut: 'cubic-bezier(0.16, 1, 0.3, 1)',

  // 链接
  colorLinkHover: '#059669',
  colorLinkActive: '#047857'
}

/** 暗色主题 Token */
export const darkToken: ThemeConfig['token'] = {
  ...lightToken,
  colorPrimary: BRAND_PRIMARY_DARK,
  colorInfo: BRAND_PRIMARY_DARK,
  colorLink: BRAND_PRIMARY_DARK,
  colorLinkHover: '#6EE7B7',
  colorLinkActive: '#A7F3D0',

  // 暗色背景层次（从深到浅）：
  //   layout(#0B1120) < sider(#111827) < container/card(#1E293B) < elevated(#273449)
  colorTextBase: '#F1F5F9',
  colorBgBase: '#0B1120',
  colorBgLayout: '#0B1120', // body 背景（最深）
  colorBgContainer: '#1E293B', // card 背景（中等）
  colorBgElevated: '#273449', // dropdown/modal 背景（略亮）
  colorBorder: '#334155', // 主边框（Slate-700，可见）
  colorBorderSecondary: '#233047', // 次级边框（比 container 略深，可见的弱分隔）

  // 文本层级（暗色）
  colorText: '#F1F5F9',
  colorTextSecondary: '#CBD5E1',
  colorTextTertiary: '#94A3B8',
  colorTextQuaternary: '#64748B',

  // 暗色阴影更深更明显
  boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.5), 0 1px 3px -1px rgba(0, 0, 0, 0.35)',
  boxShadowSecondary: '0 8px 24px -4px rgba(0, 0, 0, 0.65), 0 4px 8px -2px rgba(0, 0, 0, 0.45)'
}

/** Refine ThemedLayout 用的算法 */
export const lightAlgorithm = antdTheme.defaultAlgorithm
export const darkAlgorithm = antdTheme.darkAlgorithm

/** 全局组件配置（亮色） */
export const componentConfig: ThemeConfig['components'] = {
  // Button：边框更细，hover 时填充
  Button: {
    controlHeight: 36,
    controlHeightLG: 44,
    paddingInline: 16,
    paddingInlineLG: 24,
    fontWeight: 500,
    primaryShadow: 'none',
    defaultBorderColor: '#E2E8F0',
    defaultBg: '#FFFFFF',
    defaultHoverBg: '#F8FAFC',
    defaultHoverColor: '#0F172A',
    defaultHoverBorderColor: '#CBD5E1'
  },
  // Card：有边框 + 明显阴影（突出于 body 背景）
  Card: {
    headerBg: 'transparent',
    headerFontSize: 15,
    headerHeight: 52,
    paddingLG: 20,
    boxShadowTertiary: '0 2px 8px -2px rgba(15, 23, 42, 0.1), 0 1px 3px -1px rgba(15, 23, 42, 0.06)'
  },
  // Table：表头加深，行 hover 加深（与 body 区分）
  Table: {
    headerBg: '#F1F5F9', // 表头（Slate-100，比 body #F8FAFC 深）
    headerColor: '#475569',
    headerSplitColor: 'transparent',
    rowHoverBg: '#F1F5F9', // 行 hover（与表头一致，突出当前行）
    cellPaddingBlock: 12,
    cellPaddingInline: 16,
    borderColor: '#F1F5F9'
  },
  // Input：清晰边框
  Input: {
    controlHeight: 36,
    activeShadow: '0 0 0 3px rgba(99, 102, 241, 0.12)',
    hoverBorderColor: '#94A3B8'
  },
  // Tag：圆角小，背景柔
  Tag: {
    borderRadiusSM: 4,
    defaultBg: '#F1F5F9'
  },
  // Menu：浅色侧边栏
  Menu: {
    itemBg: 'transparent',
    itemSelectedBg: 'rgba(16, 185, 129, 0.12)',
    itemSelectedColor: '#10B981',
    itemHoverBg: 'rgba(15, 23, 42, 0.04)',
    itemHeight: 40,
    iconSize: 16,
    itemMarginInline: 8,
    itemBorderRadius: 6
  },
  // Layout：sider/header 用自定义色（介于 body 和 card 之间）
  Layout: {
    siderBg: LIGHT_SIDER_BG,
    headerBg: LIGHT_SIDER_BG,
    headerHeight: 56,
    headerPadding: '0 24px',
    bodyBg: '#F8FAFC'
  }
}

/** 暗色组件配置覆盖 */
export const darkComponentConfig: NonNullable<ThemeConfig['components']> = {
  ...componentConfig,
  Button: {
    ...componentConfig.Button!,
    defaultBorderColor: '#334155',
    defaultBg: '#1E293B',
    defaultHoverBg: '#334155',
    defaultHoverColor: '#F1F5F9',
    defaultHoverBorderColor: '#475569'
  },
  Card: {
    ...componentConfig.Card!,
    boxShadowTertiary: '0 2px 8px -2px rgba(0, 0, 0, 0.5), 0 1px 3px -1px rgba(0, 0, 0, 0.35)'
  },
  Table: {
    ...componentConfig.Table!,
    headerBg: '#283549',
    headerColor: '#CBD5E1',
    rowHoverBg: '#283549',
    borderColor: '#334155'
  },
  Tag: {
    ...componentConfig.Tag!,
    defaultBg: '#273449'
  },
  Menu: {
    ...componentConfig.Menu!,
    itemSelectedBg: 'rgba(52, 211, 153, 0.18)',
    itemSelectedColor: '#34D399',
    itemHoverBg: 'rgba(255, 255, 255, 0.06)'
  },
  Layout: {
    ...componentConfig.Layout!,
    siderBg: DARK_SIDER_BG,
    headerBg: DARK_SIDER_BG,
    bodyBg: '#0B1120'
  }
}

/** 完整亮色主题配置 */
export const lightTheme: ThemeConfig = {
  token: lightToken,
  components: componentConfig,
  algorithm: lightAlgorithm,
  cssVar: { key: 'license-admin' }
}

/** 完整暗色主题配置 */
export const darkTheme: ThemeConfig = {
  token: darkToken,
  components: darkComponentConfig,
  algorithm: darkAlgorithm,
  cssVar: { key: 'license-admin' }
}
